/* Atlas V3 Desktop — Calendar (earnings). Rides the live earnings feed
   (useEarnings, future-only) joined with Atlas names. Timeline-spine layout:
   day nodes on a left hairline, BEFORE OPEN / AFTER CLOSE clusters within
   each day, watchlist names badged with an amber dot, and live 1D % + a
   matching 1D spark filling the row. Tap a ticker to open the dock. */
import { useMemo } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { Spark, intent, useTicker } from '@/v2/v3/lib'
import { useEarnings } from '@/v2/hooks/useSupabaseData'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { Crossfade, SkelRowD, SkelText, enterStyle } from '../qol'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
// Session clusters, rendered in market order. dmt/unknown fall into "During market".
const SESSIONS = [
  ['bmo', 'BEFORE OPEN'],
  ['dmt', 'DURING MARKET'],
  ['amc', 'AFTER CLOSE'],
]

function dayLabel(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00')
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const diff = Math.round((d - today) / 86400000)
    const base = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    if (diff === 0) return `${base} · TODAY`
    if (diff === 1) return `${base} · TOMORROW`
    return base
  } catch { return dateStr }
}
function pct(v) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }

export function Calendar() {
  const openTicker = useTicker()
  const { earnings, loading: earnLoading } = useEarnings()
  const { stocks } = useAtlasStocks({ includeInactive: true })
  const { watchlists } = useWatchlists()
  const nameFor = useMemo(() => Object.fromEntries(stocks.map((s) => [s.ticker, s.name || s.company])), [stocks])
  const watched = useMemo(() => new Set((watchlists || []).flatMap((w) => w.tickers || [])), [watchlists])

  const groups = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const future = (earnings || []).filter((e) => { try { return new Date(e.date + 'T00:00:00') >= today } catch { return false } })
    future.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    const by = {}
    for (const e of future) (by[e.date] = by[e.date] || []).push(e)
    return Object.entries(by)
  }, [earnings])

  // One live batch over the week's reporters (~20-40 names) for the 1D column.
  const weekTickers = useMemo(() => [...new Set(groups.flatMap(([, rows]) => rows.map((e) => e.ticker)))], [groups])
  const { prices } = useLivePrices(weekTickers)
  // 1D sparks to match the 1D % column (was the fixed ~10-day /api/sparks
  // shape); the reporter list is small (~20-40), so a per-ticker fetch is cheap.
  const { barsByTicker } = useTickerBars(weekTickers, '1D')

  return (
    <>
      <div className="v3web-phead">
        <div className="h" style={{ fontStyle: 'italic' }}>Calendar</div>
      </div>
      {groups.length === 0 && earnLoading && (
        <Crossfade loading skeleton={
          <div style={{ position: 'relative', paddingLeft: 26 }}>
            <span aria-hidden style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 1, background: 'hsl(var(--studio-border) / 0.6)' }} />
            {[0, 1].map((g) => (
              <div key={g} style={{ position: 'relative', marginBottom: 28 }}>
                <span aria-hidden style={{ position: 'absolute', left: -26, top: 5, width: 9, height: 9, marginLeft: 0.5, borderRadius: '50%', background: 'hsl(var(--studio-background))', border: '1.5px solid hsl(var(--studio-border))' }} />
                <SkelText w={170} h={10} style={{ marginBottom: 12 }} />
                {[0, 1, 2].map((i) => <SkelRowD key={i} i={i} spark={g === 1} />)}
              </div>
            ))}
          </div>
        } />
      )}
      {groups.length === 0 && !earnLoading && <div className="v3web-empty"><div className="pe-k">No upcoming earnings</div><p>The earnings calendar populates every Sunday.</p></div>}

      {/* timeline spine — day nodes on a left hairline */}
      <div className="v3web-list" style={{ position: 'relative', paddingLeft: 26 }}>
        <span aria-hidden style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 1, background: 'hsl(var(--studio-border) / 0.6)' }} />
        {groups.map(([date, rows]) => {
          const clusters = SESSIONS
            .map(([key, label]) => [label, rows.filter((e) => (key === 'dmt' ? (e.time !== 'bmo' && e.time !== 'amc') : e.time === key))])
            .filter(([, arr]) => arr.length)
          return (
            <section key={date} style={{ position: 'relative', marginBottom: 28 }}>
              {/* date node on the spine */}
              <span aria-hidden style={{ position: 'absolute', left: -26, top: 5, width: 9, height: 9, marginLeft: 0.5, borderRadius: '50%', background: 'hsl(var(--studio-background))', border: '1.5px solid hsl(var(--studio-accent))' }} />
              <div className="v3web-kicker" style={{ marginBottom: 4 }}>{dayLabel(date)}</div>
              {clusters.map(([label, arr]) => (
                <div key={label} style={{ margin: '10px 0 2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 2px 10px', borderLeft: '2px solid hsl(var(--studio-border))' }}>
                    <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))' }}>{label}</span>
                    <span style={{ ...Mono, fontSize: 8.5, color: 'hsl(var(--studio-text-subtle))' }}>· {arr.length}</span>
                  </div>
                  {arr.map((e, ri) => {
                    const c = prices[e.ticker]?.change1d
                    const up = (c ?? 0) >= 0
                    const isWatched = watched.has(e.ticker)
                    return (
                      <div key={e.ticker + date} className="v3web-row" onClick={() => openTicker(e.ticker)} style={enterStyle(ri)}>
                        <StockLogo ticker={e.ticker} size={26} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="nm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {e.ticker}
                            {isWatched && <span title="On your watchlist" style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-accent))', flexShrink: 0 }} />}
                            <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 12, fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameFor[e.ticker] || ''}</span>
                          </div>
                          {e.quarter && <div className="sub">{e.quarter}</div>}
                        </div>
                        <Spark data={barsByTicker[e.ticker]} up={up} width={56} height={22} />
                        <div className="v3web-num" style={{ fontSize: 12.5, color: c == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(up)}))`, width: 72, textAlign: 'right' }}>{pct(c)}</div>
                        <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', border: '1px solid hsl(var(--studio-border))', borderRadius: 3, padding: '2px 6px', flexShrink: 0 }}>EARNINGS</div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </section>
          )
        })}
      </div>
    </>
  )
}
